import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import jwtDecode from 'jwt-decode';
import { RootStyle, MainStyle } from 'theme/layouts';
import { DashboardSidebar } from './Sidebar';
import { Box, useTheme } from '@mui/material';
import { SubMenu } from './SubMenu';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	setCategory,
	setFeature,
	setPages,
	setSubMenu,
	setPermission,
	getSubMenu,
} from 'state/reducers/content.reducers';
import { loginUser } from 'state/reducers/user.reducers';
import { getToken } from 'state/reducers/auth.reducers';
import { getFeaturesByCategory, getPagesByFeature } from 'services/content.service';
import { getMyPermissions } from 'services/auth.service';
import { pagesEnum, sideBarSystemManagementItem } from 'constants/ui.constants';
import WaitingMessage from 'components/WaitingMessage';

export const DashboardLayout = (props: { noCurrency?: boolean; nosidebar?: boolean }) => {
	const [open, setOpen] = useState(true);
	const [profileSubmenuOpen, setProfileSubmenuOpen] = useState(false);
	const [mobilOpen, setMobilOpen] = useState(false);
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [subMenAnchorEl, setSubMenuAnchorEl] = useState<any>(null);
	const openSubMenuDiv = Boolean(subMenAnchorEl);
	const isLoading = useAppSelector((state) => {
		const {
			auth,
			balance,
			company,
			content,
			customerAccount,
			dashboard,
			global,
			newAccount,
			pendingRequests,
			businessModel,
			reports,
			translation,
			ui,
			user,
			featureFlags,
			systemSettings,
		} = state;
		const isLoadings = [
			auth.loading,
			balance.isLoading,
			company.isLoading,
			content.loading,
			customerAccount.loading,
			dashboard.loading,
			global.loading,
			newAccount.isLoading,
			pendingRequests.loading,
			businessModel.loading,
			reports.loading,
			translation.loading,
			featureFlags.loading,
			systemSettings.loading,
		];
		return !!isLoadings.find((val) => val);
	});

	const token = useAppSelector(getToken);
	useEffect(() => {
		if (token) {
			const decodedToken = jwtDecode(token);
			dispatch(loginUser(decodedToken));
			dispatch(getMyPermissions());
		}
	}, [token]);

	const handleSubMenuClick = (subMenuItem: any) => {
		if (subMenuItem.category) {
			dispatch(setCategory(subMenuItem.category));
			if (sideBarSystemManagementItem?.children?.includes(subMenuItem)) {
				setSubMenuAnchorEl(sideBarSystemManagementItem);
				dispatch(setSubMenu({ items: sideBarSystemManagementItem.children }));
			}
			if (subMenuItem.feature) {
				getSelectedPages(subMenuItem);
			} else {
				dispatch(getFeaturesByCategory(subMenuItem.category));
			}
		} else {
			navigate(subMenuItem.link);
		}
	};

	const handleMainMenuClickWithNoSub = (menuItem: any) => {
		dispatch(setPages({ menuItem }));
		navigate(menuItem.link);
	};

	const getSelectedPages = (subMenuItem: any) => {
		dispatch(setPages({}));
		dispatch(setFeature(subMenuItem.displayTitle));
		dispatch(setPermission(subMenuItem.permissions));
		dispatch(getPagesByFeature(subMenuItem.category, subMenuItem.feature));
		navigate(`/${pagesEnum.CONTENT_PAGE}`);
	};

	const openSubMenu = (sideBarItem: any) => {
		if (subMenAnchorEl === sideBarItem) {
			handleClose();
		} else {
			setSubMenuAnchorEl(sideBarItem);
			dispatch(setSubMenu({ items: sideBarItem.children }));
		}
	};

	const handleClose = () => {
		setSubMenuAnchorEl(null);
		dispatch(setCategory(null));
		dispatch(setSubMenu({ items: [] }));
	};

	const handleProfileIconClick = (newProfileSubmenuOpenState: boolean) => {
		setProfileSubmenuOpen(newProfileSubmenuOpenState);
		if (newProfileSubmenuOpenState && !open) {
			setOpen(true);
		}
	};

	return (
		<RootStyle>
			<CssBaseline />
			{!props.nosidebar && (
				<>
					<Box
						sx={{
							background: theme.palette.root.basicDeeoGreen,
							borderRadius: open ? '0px 8px 8px 0px' : !openSubMenuDiv ? '0px 8px 8px 0px' : '0px',
							[theme.breakpoints.down('sm')]: { display: 'none' },
						}}
					>
						<DashboardSidebar
							anchorEl={anchorEl}
							setAnchorEl={setAnchorEl}
							open={open}
							profileSubmenuOpen={profileSubmenuOpen}
							handleProfileIconClick={handleProfileIconClick}
							toggleSidebar={(open: boolean) => {
								setOpen(open);
								if (!open && profileSubmenuOpen) {
									setProfileSubmenuOpen(false);
								}
							}}
							handleSubMenuClick={handleSubMenuClick}
							handleMainMenuClickWithNoSub={handleMainMenuClickWithNoSub}
							openSubMenu={openSubMenu}
							subMenAnchorEl={subMenAnchorEl}
							openSubMenuDiv={openSubMenuDiv}
						/>
					</Box>
					<Box sx={{ [theme.breakpoints.up('sm')]: { display: 'none' } }}>
						<DashboardSidebar
							anchorEl={anchorEl}
							open={mobilOpen}
							profileSubmenuOpen={profileSubmenuOpen}
							handleProfileIconClick={handleProfileIconClick}
							setAnchorEl={setAnchorEl}
							mobile={true}
							toggleSidebar={() => setMobilOpen(!mobilOpen)}
							handleSubMenuClick={handleSubMenuClick}
							openSubMenu={openSubMenu}
							subMenAnchorEl={subMenAnchorEl}
							openSubMenuDiv={openSubMenuDiv}
						/>
					</Box>
					{!open && !isLoading && (
						<SubMenu
							handleClick={handleSubMenuClick}
							open={!open}
							styleObj={{ borderRadius: '0px 8px 8px 0px', width: '180px' }}
						/>
					)}
				</>
			)}
			<MainStyle>
				{isLoading && <WaitingMessage />}
				<Outlet />
			</MainStyle>
		</RootStyle>
	);
};
